import { useEffect, useState, useMemo, useCallback } from "react";
import { Link } from "react-router-dom";
import api, { errorMessage } from "../lib/api";
import { useAuth } from "../contexts/AuthContext";
import { PageHeader, ActionButton, GhostButton, PillBadge } from "../components/Primitives";
import { AlertOctagon, FileSignature, Wrench, RefreshCw, CheckCircle2, XCircle, ExternalLink, Inbox, Eye } from "lucide-react";
import { toast } from "sonner";

const KIND_META = {
  incident:    { label: "Incident",    icon: AlertOctagon,   endpoint: "/incidents",              detailPath: "/incidents",   tone: "red" },
  dar:         { label: "DAR",         icon: FileSignature,  endpoint: "/dar",                    detailPath: "/dar",         tone: "blue" },
  maintenance: { label: "Maintenance", icon: Wrench,         endpoint: "/maintenance-requests",   detailPath: "/maintenance", tone: "amber" },
};

const SEVERITY_TONE = { low: "muted", medium: "amber", high: "red", critical: "red" };

const relativeAge = (iso) => {
  if (!iso) return "—";
  const d = new Date(iso);
  const diff = Math.round((Date.now() - d.getTime()) / 60000);
  if (diff < 1) return "just now";
  if (diff < 60) return `${diff}m ago`;
  const h = Math.round(diff / 60);
  if (h < 24) return `${h}h ago`;
  return `${Math.round(h / 24)}d ago`;
};

export default function PendingApprovals() {
  const { user } = useAuth();
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [kindFilter, setKindFilter] = useState("all");
  const [rejectingId, setRejectingId] = useState(null);
  const [rejectNote, setRejectNote] = useState("");
  const [busyId, setBusyId] = useState(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const res = await api.get("/approvals/pending");
      setRows(res.data || []);
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    load();
    // Light polling — 60s keeps the queue fresh without hammering the API.
    const id = setInterval(load, 60000);
    return () => clearInterval(id);
  }, [load]);

  const filtered = useMemo(
    () => (kindFilter === "all" ? rows : rows.filter((r) => r.kind === kindFilter)),
    [rows, kindFilter],
  );

  const counts = useMemo(() => ({
    all:         rows.length,
    incident:    rows.filter((r) => r.kind === "incident").length,
    dar:         rows.filter((r) => r.kind === "dar").length,
    maintenance: rows.filter((r) => r.kind === "maintenance").length,
  }), [rows]);

  const act = async (row, verb, notes) => {
    setBusyId(`${row.kind}-${row.id}`);
    try {
      await api.post(`${KIND_META[row.kind].endpoint}/${row.id}/${verb}`, { notes: notes || null });
      toast.success(`${KIND_META[row.kind].label} ${verb === "approve" ? "approved" : "rejected"}`);
      // Optimistic: drop the row locally; poll will reconcile in 60s.
      setRows((prev) => prev.filter((r) => !(r.kind === row.kind && r.id === row.id)));
      setRejectingId(null);
      setRejectNote("");
    } catch (err) {
      toast.error(errorMessage(err));
    } finally {
      setBusyId(null);
    }
  };

  // Soft-claim: when the user opens the Reject box (or clicks the Claim
  // button) we POST a claim so other approvers see 'REVIEWING · Jane D.'
  // on their queue. Claims auto-expire server-side after 5min, so no need
  // to release on route change.
  const claim = useCallback(async (row) => {
    try {
      const res = await api.post(`/approvals/${row.kind}/${row.id}/claim`);
      if (res.data?.conflict) {
        toast.info(`Already being reviewed by ${res.data.claimed_by_name}`);
      }
      // Optimistically merge the claim into local state so it renders
      // immediately without waiting for the next poll.
      setRows((prev) => prev.map((r) => (
        r.kind === row.kind && r.id === row.id
          ? { ...r, claimed_by: res.data?.claimed_by || user?.id, claimed_by_name: res.data?.claimed_by_name || user?.name || user?.email, claimed_at: res.data?.claimed_at || new Date().toISOString() }
          : r
      )));
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("claim failed", err);
    }
  }, [user]);

  const release = useCallback(async (row) => {
    try {
      await api.post(`/approvals/${row.kind}/${row.id}/release`);
      setRows((prev) => prev.map((r) => (
        r.kind === row.kind && r.id === row.id
          ? { ...r, claimed_by: null, claimed_by_name: null, claimed_at: null }
          : r
      )));
    } catch (err) {
      if (process.env.NODE_ENV !== "production") console.warn("release failed", err);
    }
  }, []);

  const openReject = (row) => {
    setRejectingId(`${row.kind}-${row.id}`);
    setRejectNote("");
    claim(row);
  };

  const cancelReject = (row) => {
    setRejectingId(null);
    setRejectNote("");
    // Best-effort release so the row unlocks immediately for peers.
    if (row?.claimed_by === user?.id) release(row);
  };

  return (
    <div className="p-6 md:p-10 space-y-6" data-testid="pending-approvals-page">
      <PageHeader
        title="Pending Approvals"
        subtitle="Every incident, DAR, and maintenance report waiting on a supervisor sign-off. Anyone with supervisor scope or higher can approve — first to click wins."
        action={
          <GhostButton onClick={load} data-testid="pending-approvals-refresh">
            <RefreshCw className={`size-3.5 ${loading ? "animate-spin" : ""}`} /> Refresh
          </GhostButton>
        }
        testId="pending-approvals-header"
      />

      {/* Filter chips */}
      <div className="flex items-center gap-2 flex-wrap" data-testid="pending-approvals-filters">
        {[
          { key: "all",         label: "All" },
          { key: "incident",    label: "Incidents" },
          { key: "dar",         label: "DARs" },
          { key: "maintenance", label: "Maintenance" },
        ].map((f) => {
          const active = kindFilter === f.key;
          return (
            <button
              key={f.key}
              data-testid={`pending-filter-${f.key}`}
              onClick={() => setKindFilter(f.key)}
              className={`inline-flex items-center gap-2 px-3 py-1.5 text-xs uppercase tracking-widest border transition-colors ${
                active
                  ? "border-[#FF3B30] text-[#FF3B30]"
                  : "border-[#27272A] text-zinc-400 hover:text-white hover:border-zinc-500"
              }`}
            >
              {f.label}
              <span className={`text-[10px] font-mono px-1 ${active ? "bg-[#FF3B30]/10" : "bg-zinc-800"}`}>
                {counts[f.key] ?? 0}
              </span>
            </button>
          );
        })}
      </div>

      {/* Queue table */}
      {loading && rows.length === 0 ? (
        <div className="p-10 text-center text-zinc-500">Loading pending queue…</div>
      ) : filtered.length === 0 ? (
        <div className="border border-[#27272A] bg-[#0A0A0A] p-10 text-center" data-testid="pending-approvals-empty">
          <Inbox className="size-8 mx-auto text-zinc-600 mb-2" />
          <div className="text-zinc-300">Inbox zero</div>
          <div className="text-xs text-zinc-500 mt-1">Nothing waiting for approval right now.</div>
        </div>
      ) : (
        <div className="border border-[#27272A] bg-[#0A0A0A] overflow-hidden">
          <table className="w-full text-sm" data-testid="pending-approvals-table">
            <thead className="bg-[#121212] text-[10px] uppercase tracking-widest text-zinc-500">
              <tr>
                <th className="text-left px-4 py-3">Kind</th>
                <th className="text-left px-4 py-3">Report</th>
                <th className="text-left px-4 py-3">Site</th>
                <th className="text-left px-4 py-3">Reported by</th>
                <th className="text-left px-4 py-3">Trigger</th>
                <th className="text-left px-4 py-3">Age</th>
                <th className="text-right px-4 py-3">Action</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-[#1c1c1e]">
              {filtered.map((r) => {
                const meta = KIND_META[r.kind];
                const Icon = meta.icon;
                const busy = busyId === `${r.kind}-${r.id}`;
                const isRejecting = rejectingId === `${r.kind}-${r.id}`;
                const claimedByMe = r.claimed_by && r.claimed_by === user?.id;
                const claimedByOther = r.claimed_by && !claimedByMe;
                return (
                  <tr key={`${r.kind}-${r.id}`} className={`hover:bg-[#101014] ${claimedByOther ? "opacity-70" : ""}`} data-testid={`pending-row-${r.kind}-${r.id}`}>
                    <td className="px-4 py-3">
                      <span className="inline-flex items-center gap-1.5 text-[11px] uppercase tracking-widest">
                        <Icon className="size-3.5" strokeWidth={2.2} /> {meta.label}
                      </span>
                    </td>
                    <td className="px-4 py-3 max-w-md">
                      <Link to={meta.detailPath} className="text-zinc-100 hover:text-white truncate block" title={r.title}>
                        {r.title}
                      </Link>
                      {r.description && (
                        <div className="text-[11px] text-zinc-500 truncate mt-0.5" title={r.description}>{r.description}</div>
                      )}
                      {r.claimed_by_name && (
                        <div className="mt-1.5 inline-flex items-center gap-1 text-[10px] uppercase tracking-widest font-mono" data-testid={`pending-claim-pill-${r.kind}-${r.id}`}>
                          <Eye className="size-3" strokeWidth={2.5} />
                          <span className={claimedByMe ? "text-emerald-400" : "text-amber-400"}>
                            {claimedByMe ? "You're reviewing" : `Reviewing · ${r.claimed_by_name}`}
                          </span>
                        </div>
                      )}
                    </td>
                    <td className="px-4 py-3 text-zinc-300">{r.site_name}</td>
                    <td className="px-4 py-3 text-zinc-300">{r.guard_name || "—"}</td>
                    <td className="px-4 py-3">
                      {r.severity && <PillBadge tone={SEVERITY_TONE[r.severity] || "muted"}>{r.severity}</PillBadge>}
                      {r.when_trigger && <PillBadge tone="blue">{r.when_trigger.replace(/_/g, " ")}</PillBadge>}
                    </td>
                    <td className="px-4 py-3 text-zinc-400 font-mono text-xs whitespace-nowrap">{relativeAge(r.created_at)}</td>
                    <td className="px-4 py-3">
                      {isRejecting ? (
                        <div className="flex items-center gap-2 justify-end">
                          <input
                            autoFocus
                            value={rejectNote}
                            onChange={(e) => setRejectNote(e.target.value)}
                            placeholder="Reason (required)"
                            className="bg-transparent border border-[#27272A] px-2 py-1 text-xs outline-none focus:border-[#FF3B30] w-52"
                            data-testid={`pending-reject-note-${r.kind}-${r.id}`}
                            onKeyDown={(e) => e.key === "Enter" && rejectNote.trim() && act(r, "reject", rejectNote.trim())}
                          />
                          <ActionButton
                            data-testid={`pending-reject-confirm-${r.kind}-${r.id}`}
                            disabled={busy || !rejectNote.trim()}
                            onClick={() => act(r, "reject", rejectNote.trim())}
                          >
                            {busy ? "…" : "Reject"}
                          </ActionButton>
                          <GhostButton onClick={() => cancelReject(r)}>Cancel</GhostButton>
                        </div>
                      ) : (
                        <div className="flex items-center gap-2 justify-end">
                          <Link to={meta.detailPath} className="text-zinc-500 hover:text-white text-xs inline-flex items-center gap-1" title="Open in full page">
                            <ExternalLink className="size-3" />
                          </Link>
                          {!r.claimed_by && (
                            <GhostButton
                              data-testid={`pending-claim-${r.kind}-${r.id}`}
                              onClick={() => claim(r)}
                              title="Signal to other approvers that you're reviewing this row"
                            >
                              <Eye className="size-3.5" /> Claim
                            </GhostButton>
                          )}
                          {claimedByMe && (
                            <GhostButton
                              data-testid={`pending-release-${r.kind}-${r.id}`}
                              onClick={() => release(r)}
                              title="Release your review lock"
                            >
                              <Eye className="size-3.5" /> Release
                            </GhostButton>
                          )}
                          <ActionButton
                            data-testid={`pending-approve-${r.kind}-${r.id}`}
                            disabled={busy}
                            onClick={() => act(r, "approve", null)}
                          >
                            <CheckCircle2 className="size-3.5" /> {busy && busyId === `${r.kind}-${r.id}` ? "…" : "Approve"}
                          </ActionButton>
                          <GhostButton
                            data-testid={`pending-reject-${r.kind}-${r.id}`}
                            onClick={() => openReject(r)}
                          >
                            <XCircle className="size-3.5" /> Reject
                          </GhostButton>
                        </div>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}
